/**
 * ═══════════════════════════════════════════════════════════════════════
 * UNIVERSAL PICKER - PRICE CALCULATOR
 * ═══════════════════════════════════════════════════════════════════════
 *
 * Automatically calculates and displays per-bottle/per-unit pricing
 * Works with any product by detecting pack sizes from variant data
 *
 * Subscription default: ALL products default to subscription.
 * Opt-out: add "-otp" suffix to product handle to default to one-time.
 *
 * @version 1.4.0
 * @author Nikita (BioVitals Dev Team)
 */

(function () {
  "use strict";

  /**
   * Main picker controller
   */
  class UniversalPicker {
    constructor(formElement) {
      this.form = formElement;
      this.variantData = this.loadVariantData();

      if (!this.variantData) {
        console.warn("[Picker] No variant data found");
        return;
      }

      // Find 3-bottle variant, fallback to first variant if not found
      const threeBottleVariant = this.findVariantByPackSize(3);
      this.selectedVariantId = threeBottleVariant?.id || this.variantData.variants[0]?.id;

      // All products default to subscription unless handle ends with "-otp"
      const subscribeByDefault = this.isSubscriptionProduct();

      const checkbox = formElement.querySelector("[data-subscription-checkbox]");
      if (checkbox) {
        checkbox.checked = subscribeByDefault;
      }

      this.isSubscriptionEnabled = checkbox ? checkbox.checked : true;

      this.init();
    }

    /**
     * Determines if this product should default to subscription.
     * All products are subscription-default UNLESS handle ends with "-otp".
     * @returns {boolean}
     */
    isSubscriptionProduct() {
      const handle = this.getProductHandle();
      if (!handle) return true;

      return !handle.endsWith("-otp");
    }

    /**
     * Gets the product handle from the form's data attribute or the URL.
     * @returns {string|null}
     */
    getProductHandle() {
      // First try form data attribute
      const handle = this.form.dataset.productHandle;
      if (handle) return handle;

      // Fallback: extract from URL path
      const path = window.location.pathname;
      const match = path.match(/\/products\/([^/?#]+)/);
      return match ? match[1] : null;
    }

    /**
     * Load variant data from JSON script tag
     */
    loadVariantData() {
      const dataScript = this.form.querySelector("[data-picker-variants]");
      if (!dataScript) return null;

      try {
        return JSON.parse(dataScript.textContent);
      } catch (e) {
        console.error("[Picker] Failed to parse variant data:", e);
        return null;
      }
    }

    /**
     * Find variant by pack size
     */
    findVariantByPackSize(packSize) {
      if (!this.variantData || !this.variantData.variants) return null;
      return this.variantData.variants.find(
        (v) => v.pack_size === packSize,
      );
    }

    /**
     * Initialize picker functionality
     */
    init() {

      // Set up event listeners first (needed for selection)
      this.setupRadioButtons();
      this.setupSubscriptionToggle();
      this.setupFormSubmission();

      // Select the default variant (3-bottle) visually and in form
      this.selectDefaultVariant();

      // Update prices immediately
      this.updateAllPrices();

      // Update subscription visibility
      this.updateSubscriptionVisibility();

    }

    /**
     * Select the default variant (3-bottle) visually and in form
     */
    selectDefaultVariant() {
      if (!this.selectedVariantId) return;

      // Check the radio button for the selected variant
      const radio = this.form.querySelector(
        `input[name="picker-selection"][value="${this.selectedVariantId}"]`,
      );
      if (radio) {
        radio.checked = true;
      }

      // Update visual selection
      this.updateBarSelection(this.selectedVariantId);

      // Update form inputs
      this.updateFormInputs();
    }

    /**
     * Calculate per-bottle price
     */
    calculatePerBottlePrice(variant) {
      const packSize = variant.pack_size || 1;
      const totalPrice = variant.price / 100; // Convert cents to dollars
      const perBottle = totalPrice / packSize;

      return {
        perBottle: perBottle.toFixed(2),
        total: totalPrice.toFixed(2),
        packSize: packSize,
      };
    }

    /**
     * Determine price suffix (bottle vs unit vs day)
     */
    getPriceSuffix(variant) {
      if (variant.unit_type) return "/" + variant.unit_type;
      const title = variant.title.toLowerCase();
      if (title.includes("bag")) return "/bag";
      if (title.includes("bottle")) return "/bottle";
      return "/unit";
    }

    /**
     * Pick the selling plan whose frequency matches the variant's pack size.
     * 1 pack → "1 month", 3 pack → "3 month", 6 pack → "6 month".
     * Falls back to the pre-set selling_plan_id if no name match is found.
     */
    getMatchingSellingPlan(variant) {
      const packSize = variant.pack_size || 1;
      const allocations = variant.selling_plan_allocations || [];
      const target = packSize + " month";

      for (const alloc of allocations) {
        if (alloc.name && alloc.name.toLowerCase().includes(target)) {
          return alloc.selling_plan_id;
        }
      }

      return variant.selling_plan_id;
    }

    /**
     * Update all price displays
     */
    updateAllPrices() {
      // Use subscription-aware price update
      this.updatePricesForSubscription();
    }

    /**
     * Setup radio button event listeners
     */
    setupRadioButtons() {
      const radioButtons = this.form.querySelectorAll(
        'input[name="picker-selection"]',
      );

      radioButtons.forEach((radio) => {
        radio.addEventListener("change", (e) => {
          if (e.target.checked) {
            const variantId = parseInt(e.target.value);
            this.handleVariantSelection(variantId);
          }
        });
      });

      // Also handle bar clicks (not just radio)
      const bars = this.form.querySelectorAll(".m-picker-bar");
      bars.forEach((bar) => {
        bar.addEventListener("click", (e) => {
          // Don't trigger if clicking radio input directly (radio handles its own change event)
          if (e.target.type === "radio") return;

          // Prevent event bubbling
          e.preventDefault();
          e.stopPropagation();

          const variantId = parseInt(bar.dataset.variantId);
          const radio = bar.querySelector('input[type="radio"]');

          // Always update selection when clicking a bar
          if (radio) {
            radio.checked = true;
            // Trigger change event to ensure consistency
            radio.dispatchEvent(new Event("change", { bubbles: true }));
          }
        });
      });
    }

    /**
     * Handle variant selection
     */
    handleVariantSelection(variantId) {

      this.selectedVariantId = variantId;

      // Update visual selection
      this.updateBarSelection(variantId);

      // Update form inputs
      this.updateFormInputs();
    }

    /**
     * Update bar visual selection
     */
    updateBarSelection(variantId) {
      const bars = this.form.querySelectorAll(".m-picker-bar");

      // First, remove selected class from ALL bars
      bars.forEach((bar) => {
        bar.classList.remove("m-picker-bar--selected");
      });

      // Then, add selected class only to the selected bar
      bars.forEach((bar) => {
        if (parseInt(bar.dataset.variantId) === variantId) {
          bar.classList.add("m-picker-bar--selected");
        }
      });

    }

    /**
     * Setup subscription toggle
     */
    setupSubscriptionToggle() {
      const checkbox = this.form.querySelector("[data-subscription-checkbox]");

      if (!checkbox) return;

      checkbox.addEventListener("change", (e) => {
        this.isSubscriptionEnabled = e.target.checked;
        this.updateSubscriptionVisibility();
        this.updateFormInputs();
      });
    }

    /**
     * Update subscription-only content visibility
     */
    updateSubscriptionVisibility() {
      const subscriptionOnlyElements = this.form.querySelectorAll(
        ".m-subscription-only",
      );

      // Add/remove class on form for CSS targeting
      if (this.isSubscriptionEnabled) {
        this.form.classList.add("m-subscription-enabled");
        this.form.classList.remove("m-subscription-disabled");
      } else {
        this.form.classList.add("m-subscription-disabled");
        this.form.classList.remove("m-subscription-enabled");
      }

      subscriptionOnlyElements.forEach((el) => {
        if (this.isSubscriptionEnabled) {
          el.classList.add("visible");
        } else {
          el.classList.remove("visible");
        }
      });

      // ✅ UPDATE PRICES WHEN SUBSCRIPTION CHANGES
      this.updatePricesForSubscription();
    }

    /**
     * Update prices based on subscription state
     */
    updatePricesForSubscription() {
      const bars = this.form.querySelectorAll("[data-variant-id]");

      bars.forEach((bar) => {
        const priceEl = bar.querySelector(".m-picker-price");
        if (!priceEl) return;

        // Get prices from data attributes (in cents)
        const onetimePrice = parseInt(priceEl.dataset.onetimePrice) || 0;
        const subscriptionPrice =
          parseInt(priceEl.dataset.subscriptionPrice) || onetimePrice;
        const comparePrice = parseInt(priceEl.dataset.comparePrice) || 0;
        const packSize = parseInt(priceEl.dataset.packSize) || 1;

        // Choose which price to display
        let priceToShow, comparePriceToShow;

        if (this.isSubscriptionEnabled) {
          priceToShow = subscriptionPrice;
          comparePriceToShow = comparePrice;
        } else {
          priceToShow = onetimePrice;
          comparePriceToShow = comparePrice;
        }

        // Calculate per-bottle prices
        const perBottle = (priceToShow / 100 / packSize).toFixed(2);
        const comparePerBottle =
          comparePriceToShow > 0
            ? (comparePriceToShow / 100 / packSize).toFixed(2)
            : 0;

        // Calculate savings from MSRP
        let savingsPercent = 0;
        if (comparePriceToShow > priceToShow && comparePriceToShow > 0) {
          const savings = comparePriceToShow - priceToShow;
          savingsPercent = Math.round((savings / comparePriceToShow) * 100);
        }

        // Update price display
        const unitType = priceEl.dataset.unitType || "bottle";
        priceEl.innerHTML = `
      <span class="m-price-amount">$${perBottle}</span><span class="m-price-suffix">/${unitType}</span>
    `;

        // Update compare price display
        const comparePriceEl = bar.querySelector(".m-picker-compare-price");
        if (
          comparePriceEl &&
          parseFloat(comparePerBottle) > parseFloat(perBottle)
        ) {
          comparePriceEl.textContent = `$${comparePerBottle}`;
          comparePriceEl.style.display = "block";
        } else if (comparePriceEl) {
          comparePriceEl.style.display = "none";
        }

        // Update savings percentage
        const savingsEl = bar.querySelector(".m-picker-savings");
        if (savingsEl && savingsPercent > 0) {
          savingsEl.textContent = `SAVE ${savingsPercent}%`;
          savingsEl.parentElement.style.display = "flex";
        } else if (savingsEl) {
          savingsEl.textContent = `SAVE 0%`;
          savingsEl.parentElement.style.display = "none";
        }

      });
    }

    /**
     * Update hidden form inputs
     */
    updateFormInputs() {
      const variant = this.variantData.variants.find(
        (v) => v.id === this.selectedVariantId,
      );

      if (!variant) {
        console.error("[Picker] Variant not found:", this.selectedVariantId);
        return;
      }

      // Update variant ID
      const variantInput = this.form.querySelector("[data-variant-id-input]");
      if (variantInput) {
        variantInput.value = variant.id;
      }

      // Update quantity (always 1 for pack variants)
      const quantityInput = this.form.querySelector('[name="quantity"]');
      if (quantityInput) {
        quantityInput.value = 1;
      }

      // Update selling plan — match frequency to pack size
      const sellingPlanInput = this.form.querySelector(
        "[data-selling-plan-input]",
      );
      if (sellingPlanInput) {
        if (this.isSubscriptionEnabled && variant.has_selling_plan) {
          sellingPlanInput.value = this.getMatchingSellingPlan(variant);
        } else {
          sellingPlanInput.value = "";
        }
      }

    }

    /**
     * Setup form submission handler
     */
    setupFormSubmission() {
      const submitBtn = this.form.querySelector(".kaching-atc-trigger");

      if (!submitBtn) return;

      submitBtn.addEventListener("click", (e) => {
        // Final validation
        const variantInput = this.form.querySelector("[data-variant-id-input]");

        if (!variantInput || !variantInput.value) {
          console.error("[Picker] No variant selected!");
          e.preventDefault();
          return;
        }

      });
    }
  }

  /**
   * Initialize all pickers on the page
   */
  function initPickers() {
    const forms = document.querySelectorAll(".m-picker-new-form");

    forms.forEach((form) => {
      if (!form.__pickerInitialized) {
        new UniversalPicker(form);
        form.__pickerInitialized = true;
      }
    });
  }

  /**
   * Bootstrap
   */
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initPickers);
  } else {
    initPickers();
  }

  // Handle Shopify section reloads
  document.addEventListener("shopify:section:load", initPickers);

  // Expose for debugging
  window.UniversalPicker = UniversalPicker;

})();